import { useState } from "react";
import { useHistory } from "react-router-dom";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "../../../../../../setup";
import { UserModel } from "../../../../../modules/auth/models/UserModel";
import { FileProcessModel } from "../../../models/FileProcessModel";
import { CommentsMessagesModel } from "../../../models/CommentsMessagesModel";
import { CommentsMessagesWrap } from "../../CommentsMessagesWrap";
import { CommentsMessageInput } from "../../CommentsMessageInput";
import { ExpertiseRequestCard } from "./cards/ExpertiseRequestCard";
import { acceptRequest, confirmReschedule, rescheduleRequest } from "../../../../../services/expertiseAdminService";
import { showErrorAlert } from "../../../../../helpers/alert";
import { handleApiError } from "../../../../../utils/handleApiError";
import { RescheduleModal } from "../../../../myExpertise/components/RescheduleModal";
import { MessagesExpertiseInfo } from "./cards/MessagesExpertiseInfo";
import { ExpertisePartyCard } from "./cards/ExpertisePartyCard";
import { ExpertiseSteps } from "./cards/ExpertiseSteps";
import { MediaMessageList } from "../../cards/MediaMessageList";
import { LoaderOverlay } from "../../../../../hooks/loader/LoaderOverlay";

type Props = {
    conversation: any;
    messages: CommentsMessagesModel[];
    onSendMessage: (text: string, files?: File[], replyToId?: number, editingMessageId?: number) => void;
    onReact: (messageId: number, emoji: string) => void;
    onLeave: () => void;
    onLoadMore?: () => void;
    hasMore?: boolean;
    onDelete?: (comment: CommentsMessagesModel) => void;
    onRefresh?: () => void;
}

/**
 * Hilo de una solicitud de Expertise.
 *
 * Sobre las tarjetas: los mensajes de sistema (solicitud, reagenda, confirmacion) se pintan arriba
 * del feed, no como burbujas. Se mantienen fuera de CommentsMessagesWrap a proposito para no
 * modificar ese componente, que sirve a introductions, experiences y foros — tocarlo arriesgaria
 * tres pantallas ya probadas para beneficiar a una.
 */
export const ExpertiseContent = ({
    conversation, messages, onSendMessage, onReact, onLoadMore, hasMore, onDelete, onRefresh,
}: Props) => {
    const history = useHistory();
    const user = useSelector<RootState>(({ auth }) => auth.user, shallowEqual) as UserModel;
    const [replySelect, setReplySelect] = useState<CommentsMessagesModel | null>(null);
    const [editingMessage, setEditingMessage] = useState<CommentsMessagesModel | null>(null);
    const [files, setFiles] = useState<FileProcessModel[]>([]);
    const [showReschedule, setShowReschedule] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadingSteps, setLoadingSteps] = useState(false);
    // Contador que le pide a ExpertiseSteps re-fetchear su timeline. `onRefresh` refresca la
    // conversation, pero el hijo tiene un `data` propio (StepsResponse) que si no se le pica sigue
    // trayendo actions viejas y deja los botones habilitados despues de un Confirmar/Reagenda hecho
    // desde ExpertiseRequestCard.
    const [stepsRefresh, setStepsRefresh] = useState(0);

    const request = conversation?.expertiseRequestDetail ??
        conversation?.expertise_request_detail ??
        conversation?.expertiseRequest ??
        conversation?.expertise_request;
    const requestId: number | undefined = request?.id;

    // El HOST del hilo es el experto (dueño de la expertise), no el sender del mensaje de sistema.
    // El backend lo expone en `host_user_id`/`hostUserId` de la conversation; CommentsMessagesWrap
    // lo usa para decidir a que mensaje pegarle el chip HOST.
    const hostUserId: number | null =
        conversation?.hostUserId ?? conversation?.host_user_id ?? null;

    // Las acciones las ve UNICAMENTE el experto; el solicitante ve la misma tarjeta sin botones.
    // En el hilo solo hay dos partes, asi que "no soy quien lo pidio" equivale a "soy el experto"
    // — el backend no expone el id del experto en el detalle de la solicitud.
    const requester = request?.requestedBy ?? request?.requested_by;
    const requesterId = requester?.id;
    const isExpert = Boolean(requesterId && user?.id && requesterId !== user.id);

    // Estado del hilo para decidir que acciones ofrece la tarjeta de sistema.
    const isOpen = (request?.effectiveStatus ?? request?.effective_status ?? request?.status) === 'request';
    const proposedOn = request?.proposedOn ?? request?.proposed_on;
    const proposedById = (request?.proposedBy ?? request?.proposed_by)?.id;
    // Confirma quien NO puso la fecha que esta sobre la mesa. Con una reagenda viva, esa fecha la
    // puso `proposedBy`; sin reagenda, la puso el solicitante al crear la peticion — y entonces
    // confirmar equivale a aceptar la solicitud tal cual.
    const dateSetterId = proposedById ?? requesterId;
    const iSetTheDate = Boolean(dateSetterId && user?.id && dateSetterId === user.id);

    // Las acciones viven en UNA sola tarjeta, la mas reciente del hilo. Repetirlas en cada evento
    // dejaria varios RESCHEDULE apilados, todos apuntando al mismo estado.
    //
    // Se elige por id mayor, NO por posicion: `messages` llega del mas nuevo al mas viejo y
    // CommentsMessagesWrap lo voltea al pintar con `column-reverse`, asi que fiarse del orden del
    // array pone los botones en la tarjeta equivocada.
    const lastSystemId = messages.reduce<number | null>((acc, m: any) => {
        const t = m.messageType ?? m.message_type;
        if (!t || t === 'user') return acc;
        const id = Number(m.id);
        if (isNaN(id)) return acc;
        return acc === null || id > acc ? id : acc;
    }, null);

    /**
     * Del hilo se pinta UNA sola tarjeta de sistema: la mas reciente.
     *
     * Cada reagenda crea su propio mensaje —eso se mantiene, porque es lo que mueve la conversacion
     * en la lista, actualiza el preview y le avisa a la otra parte— pero mostrarlas todas apilaba
     * tarjetas que dicen lo mismo, y la de arriba ademas quedaba sin acciones. El historial sigue
     * guardado en la base por si algun dia hace falta.
     *
     * Se filtra el mensaje completo y no solo su cuerpo: dejando el cuerpo vacio quedaria el avatar,
     * el autor y el pie de reacciones flotando sin contenido.
     */
    const visibleMessages = messages.filter((m: any) => {
        const type = m.messageType ?? m.message_type;
        if (!type || type === 'user') return true;
        return Number(m.id) === lastSystemId;
    });

    const handleSend = (text: string) => {
        onSendMessage(text, files.map(f => f.file), replySelect ? Number(replySelect.id) : undefined,
            editingMessage ? Number(editingMessage.id) : undefined);
        setReplySelect(null);
        setEditingMessage(null);
        setFiles([]);
    }

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (hasMore && onLoadMore && e.currentTarget.scrollTop === 0) onLoadMore();
    }

    /**
     * Confirmar la fecha que esta sobre la mesa. Son dos endpoints distintos segun de donde venga
     * esa fecha: si hay una reagenda viva se confirma la propuesta, y si no, se acepta la solicitud
     * con la fecha original. `confirmReschedule` sin propuesta responde 400.
     */
    const handleConfirm = async () => {
        if (!requestId) return;
        // Guard idempotente. Sin este chequeo, si el timeline (ExpertiseSteps) o esta misma tarjeta
        // se pulsan dos veces seguidas, la segunda revienta con 400 "not pending". El estado local
        // es el mismo `request` que ya se pinto: si dice que no esta abierta, aqui no hay nada que
        // hacer.
        if (!isOpen) {
            showErrorAlert({
                title: 'Already answered',
                message: 'This request has already been answered.',
            });
            return;
        }
        try {
            if (proposedOn) {
                await confirmReschedule(requestId);
                showErrorAlert({ title: 'Confirmed', message: 'The new date is now agreed.' });
            } else {
                await acceptRequest(requestId);
                showErrorAlert({ title: 'Accepted', message: 'The request was accepted.' });
            }
            onRefresh?.();
            setStepsRefresh(k => k + 1);
        } catch (error: any) {
            handleApiError(error);
        }
    }

    const submitReschedule = async (proposedOn: string) => {
        if (!requestId) return;
        try {
            setSaving(true);
            await rescheduleRequest(requestId, proposedOn);
            showErrorAlert({
                title: 'Sent',
                message: 'Your proposed date was sent. It applies once the other party confirms it.',
            });
            setShowReschedule(false);
            onRefresh?.();
            setStepsRefresh(k => k + 1);
        } catch (error: any) {
            handleApiError(error);
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="messages-content-wrap">
            <div className="messages-content-comments">
                <div className="w-100">
                    <div className="help-center-sections w-100 tap-flex-center justify-content-start">
                        <div className="nb-link-underline-500 w-auto cursor-pointer" onClick={() => history.goBack()}>
                            Back
                        </div>
                    </div>
                    <div className="d-flex flex-row w-100">
                        <h3>{request?.expertise?.title || conversation.title || "Expertise"}</h3>
                    </div>
                </div>

                <div className="chat-messages-container" onScroll={handleScroll}>
                    <CommentsMessagesWrap
                        comments={visibleMessages}
                        onReact={(c) => onReact(Number(c.id), "👍")}
                        onReply={setReplySelect}
                        onEdit={(c) => setEditingMessage(c)}
                        onDelete={onDelete}
                        hostUserId={hostUserId}
                        // Un mensaje de sistema conserva avatar, autor, hora y el pie de acciones;
                        // solo cambia su cuerpo por la tarjeta. Los mensajes normales caen al
                        // texto de siempre.
                        renderBody={(c: any) => {
                            const type = c.messageType ?? c.message_type;
                            if (!type || type === 'user') return c.content;
                            return (
                                <ExpertiseRequestCard
                                    messageType={type}
                                    actorName={c.author}
                                    originalDate={request?.scheduledOn}
                                    proposedDate={request?.proposedOn}
                                    canReschedule={isOpen && Number(c.id) === lastSystemId}
                                    canConfirm={isOpen && Number(c.id) === lastSystemId && !iSetTheDate}
                                    status={request?.effectiveStatus ?? request?.effective_status ?? request?.status}
                                    onConfirm={handleConfirm}
                                    onReschedule={() => setShowReschedule(true)}
                                />
                            );
                        }}
                    />
                </div>

                <CommentsMessageInput
                    cleanReply={() => setReplySelect(null)}
                    reply={replySelect}
                    onSendComment={handleSend}
                    files={files}
                    OnRemoveFile={(fileToRemove) => setFiles(files.filter(f => f !== fileToRemove))}
                    onAddFile={(f) => setFiles([...files, { file: f, isUploading: false, resourceId: null }] as FileProcessModel[])}
                    editingMessage={editingMessage}
                    onCancelEdit={() => setEditingMessage(null)}
                />
            </div>

            <div className="messages-content-info">
                {/* La contraparte: el experto ve al miembro y el miembro ve al experto. */}
                <ExpertisePartyCard
                    name={isExpert ? requester?.name : conversation?.title}
                    avatar={isExpert ? requester?.avatar : conversation?.avatar}
                    badge={isExpert ? 'Member' : 'Expert'}
                />
                <MessagesExpertiseInfo conversation={conversation} />
                {/* El timeline. Reagendar reusa el modal que ya vive en esta pantalla en vez de
                    abrir uno propio, para que ambas entradas (chat y paso 1) compartan validacion. */}
                <ExpertiseSteps
                    requestId={requestId}
                    onChanged={onRefresh}
                    onReschedule={() => setShowReschedule(true)}
                    onLoadingChange={setLoadingSteps}
                    refreshKey={stepsRefresh}
                />
                <MediaMessageList
                    mediafiles={messages.flatMap(m => (m.files || []).map(f => ({
                        id: Number(f.id),
                        type: f.resourceType as any,
                        url: f.file,
                        filename: f.title,
                        size: f.size,
                    })))}
                />
            </div>

            {/* Cubre el cambio de hilo: los datos del chat anterior ya se limpiaron y los del
                nuevo aun no llegan, asi que sin el overlay se veria el panel a medio armar. */}
            <LoaderOverlay visible={loadingSteps} message="Loading..." />

            <RescheduleModal
                show={showReschedule}
                close={() => setShowReschedule(false)}
                currentDate={request?.scheduledOn}
                onSubmit={submitReschedule}
                loading={saving}
            />
        </div>
    )
}

export default ExpertiseContent;
